import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { wishlistService } from '../services/api';
import { useAuth } from './useAuth';

const normalizeResponseData = (response) => response?.data ?? [];

const getWishlistProductId = (item) => {
  const rawId = item?.id ?? item?.productId ?? item?.product?.id;
  const numericId = Number(rawId);
  return Number.isNaN(numericId) ? null : numericId;
};

const createWishlistStatusItem = (product) => ({
  product,
  isAvailable: product?.status === 'AVAILABLE',
  hasActiveReservation: product?.status === 'RESERVED',
});

const upsertWishlistEntry = (items, nextItem) => {
  const nextId = getWishlistProductId(nextItem);
  if (nextId == null) {
    return items;
  }

  const filteredItems = items.filter((item) => getWishlistProductId(item) !== nextId);
  return [nextItem, ...filteredItems];
};

const removeWishlistEntry = (items, productId) =>
  items.filter((item) => getWishlistProductId(item) !== productId);

export const useWishlist = ({ includeStatus = false } = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pendingProductIds, setPendingProductIds] = useState({});

  const wishlistQueryKey = ['wishlist', user?.id];
  const wishlistStatusQueryKey = ['wishlist-status', user?.id];

  const wishlistQuery = useQuery({
    queryKey: wishlistQueryKey,
    enabled: !!user,
    queryFn: async () => normalizeResponseData(await wishlistService.getWishlist()),
    placeholderData: [],
  });

  const wishlistStatusQuery = useQuery({
    queryKey: wishlistStatusQueryKey,
    enabled: !!user && includeStatus,
    queryFn: async () => normalizeResponseData(await wishlistService.getWishlistWithStatus()),
    placeholderData: [],
  });

  const wishlistItems = useMemo(() => wishlistQuery.data || [], [wishlistQuery.data]);
  const wishlistItemsWithStatus = useMemo(
    () => wishlistStatusQuery.data || [],
    [wishlistStatusQuery.data]
  );

  const wishlistStatusMap = useMemo(() => {
    const ids = new Set();

    wishlistItems.forEach((item) => {
      const productId = getWishlistProductId(item);
      if (productId != null) {
        ids.add(productId);
      }
    });

    wishlistItemsWithStatus.forEach((item) => {
      const productId = getWishlistProductId(item);
      if (productId != null) {
        ids.add(productId);
      }
    });

    return Object.fromEntries(Array.from(ids).map((productId) => [productId, true]));
  }, [wishlistItems, wishlistItemsWithStatus]);

  const updatePendingState = (productId, isPending) => {
    setPendingProductIds((previous) => ({
      ...previous,
      [productId]: isPending,
    }));
  };

  const setWishlistState = async (productId, shouldBeWishlisted, product = null) => {
    if (!user) {
      return false;
    }

    const numericProductId = Number(productId);
    const previousWishlist = queryClient.getQueryData(wishlistQueryKey);
    const previousWishlistWithStatus = queryClient.getQueryData(wishlistStatusQueryKey);

    updatePendingState(numericProductId, true);

    if (product) {
      queryClient.setQueryData(wishlistQueryKey, (current = []) => (
        shouldBeWishlisted
          ? upsertWishlistEntry(current, product)
          : removeWishlistEntry(current, numericProductId)
      ));

      queryClient.setQueryData(wishlistStatusQueryKey, (current = []) => (
        shouldBeWishlisted
          ? upsertWishlistEntry(current, createWishlistStatusItem(product))
          : removeWishlistEntry(current, numericProductId)
      ));
    }

    try {
      if (shouldBeWishlisted) {
        await wishlistService.addToWishlist(numericProductId);
      } else {
        await wishlistService.removeFromWishlist(numericProductId);
      }

      return shouldBeWishlisted;
    } catch (error) {
      queryClient.setQueryData(wishlistQueryKey, previousWishlist);
      queryClient.setQueryData(wishlistStatusQueryKey, previousWishlistWithStatus);
      throw error;
    } finally {
      updatePendingState(numericProductId, false);
      queryClient.invalidateQueries({ queryKey: wishlistQueryKey });
      queryClient.invalidateQueries({ queryKey: wishlistStatusQueryKey });
      queryClient.invalidateQueries({ queryKey: ['wishlist-cursor', user?.id] });
    }
  };

  const toggleWishlist = async (productId, product = null) => {
    const numericProductId = Number(productId);
    const nextState = !wishlistStatusMap[numericProductId];
    return setWishlistState(numericProductId, nextState, product);
  };

  return {
    wishlistItems,
    wishlistItemsWithStatus,
    wishlistStatusMap,
    pendingProductIds,
    isWishlistLoading: wishlistQuery.isLoading || (includeStatus && wishlistStatusQuery.isLoading),
    wishlistError: wishlistQuery.error || (includeStatus ? wishlistStatusQuery.error : null),
    isWishlisted: (productId) => Boolean(wishlistStatusMap[Number(productId)]),
    addToWishlist: (productId, product = null) => setWishlistState(productId, true, product),
    removeFromWishlist: (productId, product = null) => setWishlistState(productId, false, product),
    toggleWishlist,
    refetchWishlist: async () => Promise.all([
      wishlistQuery.refetch(),
      wishlistStatusQuery.refetch(),
    ]),
  };
};

export default useWishlist;
